'use client'

import React, { ReactNode } from 'react'
import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import { NavigationProvider } from '@/lib/context/navigationcontext'
import { UserContextProvider } from '@/lib/context/userContext'
import { RouteGuardProvider } from '@/lib/context/routeGuardContext'
import { PreferencesProvider } from '@/lib/context/preferencesContext'
import { BenchmarkProvider } from '@/lib/context/benchmarkContext'
import { BannerAndGoalProvider } from '@/lib/context/bannerAndGoalContext'
import { CompanyProvider } from '@/lib/context/companyContext'
import { OfficeListContextProvider } from '@/lib/context/biContext/OfficeListContext'
import { CombinedListingsProvider } from '@/lib/context/biContext'
import { GoalProvider } from '@/app/feature/business/context/goalContext'
import { CssBaseline } from '@mui/material'
import globalStyles from '@/app/styles/GlobalStyles'
import { SessionData } from '@/app/adapters/types'

/**
 * Client-side providers wrapper that extracts session and pathname from Next.js hooks
 * and passes them to lib contexts as props, decoupling lib code from Next.js dependencies
 */
export function ProvidersClientWrapper({ children }: { readonly children: ReactNode }) {
	const { data: session } = useSession()
	const pathname = usePathname()

	// Extract sessionData from session - include all necessary session info for lib contexts
	const sessionData: SessionData = {
		accessToken: session?.accessToken,
		user: {
			...(session?.user as SessionData['user']),
			email: session?.user?.email ?? undefined,
		},
		idToken: (session as SessionData)?.idToken,
		oktaId: (session as SessionData)?.oktaId,
	}

	if (pathname === '/feature/dashboard/compass-poc') {
		return (
			<NavigationProvider currentPath={pathname}>
				{globalStyles}
				<CssBaseline />
				{children}
			</NavigationProvider>
		)
	}

	return (
		<NavigationProvider currentPath={pathname}>
			<UserContextProvider>
				<CompanyProvider sessionData={sessionData}>
					<PreferencesProvider sessionData={sessionData}>
						<RouteGuardProvider>
							<BenchmarkProvider>
								<BannerAndGoalProvider>
									<GoalProvider>
										<OfficeListContextProvider sessionData={sessionData}>
											<CombinedListingsProvider sessionData={sessionData}>
												{globalStyles}
												<CssBaseline />
												{children}
											</CombinedListingsProvider>
										</OfficeListContextProvider>
									</GoalProvider>
								</BannerAndGoalProvider>
							</BenchmarkProvider>
						</RouteGuardProvider>
					</PreferencesProvider>
				</CompanyProvider>
			</UserContextProvider>
		</NavigationProvider>
	)
}
